/**
 * The site layout behind the Cleaning Visual (z8nrz7f731): buildings, their
 * floors, and which floor each room is on. Backend: `/facility`.
 */
import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { SitePlanReading } from '@/lib/sitePlanPicture'

export interface LayoutRoom {
  id: string
  /** The Room ID as shown on the Room list. */
  label: string
  typeName: string
  status: 'OPERATIONAL' | 'OUT_OF_SERVICE'
}

export interface LayoutFloor {
  id: string
  /** A floor only exists inside a building. */
  buildingId: string
  name: string
  description: string | null
  level: number
  rooms: LayoutRoom[]
}

/** A site the tenant operates (z8nrz7fw25), e.g. "NYP": set up once, picked afterwards. */
export interface Facility {
  id: string
  /** Short badge, first in every location path. */
  code: string
  name: string
  address: string | null
  description: string | null
  sortOrder: number
}

export interface FacilityInput {
  code: string
  name: string
  address?: string | null
  description?: string | null
}

export interface LayoutBuilding {
  id: string
  /** Every building belongs to a facility. Absent from a backend that predates facilities. */
  facilityId?: string
  code: string
  name: string
  description: string | null
  sortOrder: number
  /** Its place on the drawn site plan, in campus-grid cells; null = placed automatically. */
  mapX: number | null
  mapY: number | null
  mapW: number | null
  mapH: number | null
  schematicUrl: string | null
  schematicName: string | null
  floors: LayoutFloor[]
}

export interface SiteLayout {
  /** The site plan picture uploaded earlier, if any (its file name). Not shown: a picture is only read. */
  sitePlan: { name: string } | null
  /** In display order. Absent from a backend that predates facilities. */
  facilities?: Facility[]
  buildings: LayoutBuilding[]
  /** Connections between buildings, drawn between them on the plan. */
  links: BuildingLink[]
  unmappedRooms: LayoutRoom[]
}

/** One room's facts for the Overall View; its status is derived in `lib/roomStatus`. */
export interface OverviewRoom {
  id: string
  label: string
  name: string
  /** The room type, whose cycles are the room's schedule. */
  typeId: string
  typeName: string
  grade: string | null
  areaSqm: number | null
  floorId: string | null
  buildingId: string | null
  outOfService: boolean
  statusNote: string | null
  holdTimeHours: number | null
  /** The first date (within a year) the room type's cycles fall on; a schedule until tasks are generated. */
  nextCycle: { date: string; label: string } | null
  lastCleanEndedAt: string | null
  active: {
    eventId: string
    procedureId: string | null
    startedAt: string | null
    stepsDone: number
    stepsTotal: number
    currentStep: string | null
  } | null
  awaitingRelease: boolean
  sop: string | null
  operator: string | null
  nextDueDate: string | null
  overdue: boolean
  dueSoon: boolean
}

/** A piece of equipment standing in a room. */
export interface PlacedUnit {
  id: string
  label: string
  typeName: string
  purpose: string
  status: 'OPERATIONAL' | 'OUT_OF_SERVICE'
  /** Where it stands inside its room, in cells from the back-left corner; null: placed automatically. */
  spot?: { x: number; y: number } | null
  /** The shape it is drawn as; null: guessed from its type. */
  shape?: string | null
}

export interface LocationRoom extends LayoutRoom {
  /** The room's own description, else null. */
  name: string | null
  /** Floor area in m², when recorded: sizes the room in the 3D view. Absent from older backends. */
  areaSqm?: number | null
  /** Where it was dragged on its floor in the 3D view, in grid cells; null: laid out automatically. */
  plan?: { x: number; y: number } | null
  /** Its size on that grid when set by hand, in cells; null: sized from its area. */
  size?: { w: number; d: number } | null
  /** Its outline: "rect", "L", "L-left", "U" or "T". Absent from older backends: a rectangle. */
  outline?: string
  units: PlacedUnit[]
}

/**
 * Where every piece of equipment stands (z8nrz7fw25): Facility → Building →
 * Floor → Room → units. Behind `equipment.read` or `inventory.view`.
 */
export interface LocationTree {
  facilities: (Facility & {
    buildings: {
      id: string
      code: string
      name: string
      floors: { id: string; name: string; level: number; rooms: LocationRoom[] }[]
    }[]
  })[]
  /** Rooms on no floor: they can hold units, but have no address above them. */
  unmappedRooms: LocationRoom[]
  /** Equipment in no room, with its old free-text location and the room it suggests. */
  unplaced: (PlacedUnit & { hint: string | null; suggestedRoomId: string | null })[]
}

export interface BuildingInput {
  /** Omitted on create: the tenant's first facility. */
  facilityId?: string
  code: string
  name: string
  description?: string | null
  /** Placed on the drawn site plan (all four), or null for automatic placement. */
  mapX?: number | null
  mapY?: number | null
  mapW?: number | null
  mapH?: number | null
}

export type LinkKind = 'corridor' | 'bridge' | 'tunnel'

/** What was read off a site plan picture, for review before anything is saved. */
export interface SitePlanProposal {
  buildings: {
    code: string
    name: string
    description: string | null
    map: { x: number; y: number; w: number; h: number }
    /** The existing building it matched (by code, then name), or null: a new one. */
    match: { id: string; code: string; name: string } | null
  }[]
  connections: { fromCode: string; toCode: string; kind: LinkKind; label: string | null; exists: boolean }[]
}

export interface SitePlanApply {
  buildings: { code: string; name: string; description: string | null; matchId?: string; mapX: number; mapY: number; mapW: number; mapH: number }[]
  connections: { fromCode: string; toCode: string; kind: LinkKind; label: string | null }[]
}

/** Two buildings connected on the site. */
export interface BuildingLink {
  id: string
  fromId: string
  toId: string
  kind: LinkKind
  label: string | null
}

export interface FloorInput {
  name: string
  description?: string | null
  level?: number
  /** Required on create; on update, moves the floor to another building. */
  buildingId?: string
}

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({ 'Content-Type': 'application/json', 'X-Tenant-Id': tenantId, ...init?.headers }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    throw Object.assign(new Error(message || `Request failed: ${res.status}`), { status: res.status })
  }
  return res.json() as Promise<T>
}

const body = (value: unknown): RequestInit => ({ body: JSON.stringify(value) })

interface PresignedSchematic {
  url: string
  method: 'PUT'
  headers: Record<string, string>
  storageKey: string
  fileName: string
  mimeType: string
}

export const facilityApi = {
  layout: (tenantId: string) => request<SiteLayout>('/facility/layout', tenantId),
  overview: (tenantId: string) =>
    request<{ generatedAt: string; rooms: OverviewRoom[] }>('/facility/overview', tenantId),

  locations: (tenantId: string) => request<LocationTree>('/facility/locations', tenantId),

  listFacilities: (tenantId: string) => request<Facility[]>('/facility/facilities', tenantId),
  createFacility: (tenantId: string, input: FacilityInput) =>
    request<Facility>('/facility/facilities', tenantId, { method: 'POST', ...body(input) }),
  updateFacility: (tenantId: string, id: string, input: Partial<FacilityInput> & { sortOrder?: number }) =>
    request<Facility>(`/facility/facilities/${id}`, tenantId, { method: 'PATCH', ...body(input) }),
  deleteFacility: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/facility/facilities/${id}`, tenantId, { method: 'DELETE' }),

  createBuilding: (tenantId: string, input: BuildingInput) =>
    request<{ id: string }>('/facility/buildings', tenantId, { method: 'POST', ...body(input) }),
  updateBuilding: (tenantId: string, id: string, input: Partial<BuildingInput> & { sortOrder?: number }) =>
    request<{ id: string }>(`/facility/buildings/${id}`, tenantId, { method: 'PATCH', ...body(input) }),
  deleteBuilding: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/facility/buildings/${id}`, tenantId, { method: 'DELETE' }),

  /**
   * Presign, PUT the bytes straight to storage, then confirm — the equipment
   * documents' three steps. The PUT carries no Authorization header: the
   * presigned URL is its own credential, and S3 rejects a second one.
   */
  uploadSchematic: async (tenantId: string, id: string, file: File) => {
    const presigned = await request<PresignedSchematic>(`/facility/buildings/${id}/schematic/presign`, tenantId, {
      method: 'POST',
      ...body({ fileName: file.name, mimeType: file.type, sizeBytes: file.size }),
    })
    const put = await fetch(presigned.url, { method: presigned.method, headers: presigned.headers, body: file })
    if (!put.ok) throw new Error(`Upload failed (${put.status}). The schematic was not saved.`)
    return request<{ id: string }>(`/facility/buildings/${id}/schematic`, tenantId, {
      method: 'POST',
      ...body({ storageKey: presigned.storageKey, fileName: presigned.fileName, mimeType: presigned.mimeType }),
    })
  },
  removeSchematic: (tenantId: string, id: string) =>
    request<{ id: string }>(`/facility/buildings/${id}/schematic`, tenantId, { method: 'DELETE' }),

  /** Matches what was read off a site plan picture to the buildings here: a proposal; saves nothing. */
  matchSitePlan: (tenantId: string, reading: SitePlanReading) =>
    request<SitePlanProposal>('/facility/site-plan/match', tenantId, { method: 'POST', ...body(reading) }),
  /** Saves the reviewed parts of a reading into the schematic. */
  applySitePlan: (tenantId: string, input: SitePlanApply) =>
    request<{ created: number; placed: number; connected: number }>('/facility/site-plan/apply', tenantId, { method: 'POST', ...body(input) }),

  createLink: (tenantId: string, input: { fromId: string; toId: string; kind: LinkKind; label?: string }) =>
    request<BuildingLink>('/facility/links', tenantId, { method: 'POST', ...body(input) }),
  updateLink: (tenantId: string, id: string, input: { kind?: LinkKind; label?: string | null }) =>
    request<BuildingLink>(`/facility/links/${id}`, tenantId, { method: 'PATCH', ...body(input) }),
  deleteLink: (tenantId: string, id: string) => request<{ ok: true }>(`/facility/links/${id}`, tenantId, { method: 'DELETE' }),

  createFloor: (tenantId: string, input: FloorInput) =>
    request<{ id: string }>('/facility/floors', tenantId, { method: 'POST', ...body(input) }),
  updateFloor: (tenantId: string, id: string, input: Partial<FloorInput>) =>
    request<{ id: string }>(`/facility/floors/${id}`, tenantId, { method: 'PATCH', ...body(input) }),
  deleteFloor: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/facility/floors/${id}`, tenantId, { method: 'DELETE' }),

  /** A room's size in the 3D view, in cells; both null to size it from its area again. */
  setRoomSize: (tenantId: string, roomId: string, w: number | null, d: number | null, outline?: string | null) =>
    request<{ id: string }>(`/facility/rooms/${roomId}/size`, tenantId, {
      method: 'PATCH',
      ...body({ w, d, ...(outline !== undefined ? { outline } : {}) }),
    }),
  /** Where a unit stands inside its room, and its shape. Fields left out stay as they are. */
  setUnitSpot: (tenantId: string, equipmentId: string, input: { x?: number | null; y?: number | null; shape?: string | null }) =>
    request<{ id: string }>(`/facility/units/${equipmentId}/spot`, tenantId, { method: 'PATCH', ...body(input) }),
  /** Place a room on its floor's grid in the 3D view; both null for the automatic layout. */
  setRoomPosition: (tenantId: string, roomId: string, x: number | null, y: number | null) =>
    request<{ id: string; x: number | null; y: number | null }>(`/facility/rooms/${roomId}/position`, tenantId, {
      method: 'PATCH',
      ...body({ x, y }),
    }),

  setRoomFloor: (tenantId: string, roomId: string, floorId: string | null) =>
    request<{ id: string; floorId: string | null }>(`/facility/rooms/${roomId}/floor`, tenantId, {
      method: 'PATCH',
      ...body({ floorId }),
    }),
}
